import { lruMemoize } from 'reselect';

import type {
  FilesComponentSchema,
  FilesFormatSchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';

import { getIsDropFrame } from './get_is_drop_frame';
import type { TimecodeOptions } from './get_proxy_timecode_options';
import { getTrackFromFormat } from './get_track_from_format';

// Parsing functions ported from iconik_transcode - https://github.com/iconik-io/iconik_transcode/blob/307f17cc4775c7e93347e64392be9a5de82ff939/src/iconik_transcode/mediainfo.py

// https://github.com/iconik-io/iconik_transcode/blob/307f17cc4775c7e93347e64392be9a5de82ff939/src/iconik_transcode/mediainfo.py#L122
const parseFrameRate = (
  track: Record<string, string | undefined> | undefined,
): number | undefined => {
  if (track?.frame_rate !== undefined) {
    return parseFloat(track.frame_rate.split(' ')[0]);
  }
  return undefined;
};

// https://github.com/iconik-io/iconik_transcode/blob/307f17cc4775c7e93347e64392be9a5de82ff939/src/iconik_transcode/mediainfo.py#L132
const getStartTimeCode = (
  components: FilesComponentSchema[] | undefined,
): string | undefined => {
  let tcTrack = null;
  let videoTrack = null;
  let generalTrack = null;

  for (const component of components ?? []) {
    if (
      component.type === 'OTHER' &&
      component.metadata?.type === 'Time code'
    ) {
      tcTrack = component.metadata;
    }
    if (component.type === 'VIDEO') {
      videoTrack = component.metadata;
    }
    if (component.type === 'GENERAL') {
      generalTrack = component.metadata;
    }
  }

  // Check timecode in priority order
  const tc =
    tcTrack?.time_code_of_first_frame ??
    videoTrack?.time_code_of_first_frame ??
    generalTrack?.tim;

  if (tc) {
    const match = tc.match(/(\d\d)([:;,.])(\d\d)([:;,.])(\d\d)([:;,.])(\d\d)/);
    if (match) {
      if ([';', ','].includes(match[6])) {
        return `${match[1]}:${match[3]}:${match[5]};${match[7]}`;
      } else {
        return `${match[1]}:${match[3]}:${match[5]}:${match[7]}`;
      }
    }
  }

  return undefined;
};

// https://github.com/iconik-io/iconik_transcode/blob/307f17cc4775c7e93347e64392be9a5de82ff939/src/iconik_transcode/mediainfo.py#L102
const isDropFrame = (
  videoTrack: Record<string, string | undefined>,
): boolean => {
  if (videoTrack.delay_dropframe !== undefined) {
    return videoTrack.delay_dropframe === 'Yes';
  }

  if (videoTrack.delay_settings !== undefined) {
    if (videoTrack.delay_settings.includes('DropFrame=Yes')) {
      return true;
    }
  }

  return false;
};

export const getOriginalFormatTimecodeOptions = lruMemoize(
  (
    originalFormat: FilesFormatSchemaWithId | undefined,
    contentType: string,
  ): TimecodeOptions | undefined => {
    if (contentType !== 'video') {
      return {
        frameRate: contentType === 'audio' ? 1000 : 0,
        isDropFrame: false,
        startTimecode: undefined,
      };
    }
    if (!originalFormat) {
      return undefined;
    }
    const videoTrack = getTrackFromFormat(originalFormat, 'VIDEO');

    const frameRate =
      originalFormat.metadata?.[0]?.frame_rate ??
      parseFrameRate(videoTrack) ??
      25;
    const dropFrameValue = videoTrack ? isDropFrame(videoTrack) : false;
    const isDropFrameFlag = getIsDropFrame(Number(frameRate), dropFrameValue);

    return {
      frameRate: Number(frameRate),
      isDropFrame: isDropFrameFlag,
      startTimecode: getStartTimeCode(originalFormat.components),
    };
  },
);
